import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MailOpen, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-[#D4AF37] text-zinc-950",
  read: "bg-zinc-700 text-white",
  contacted: "bg-emerald-600/80 text-white",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () =>
    axios
      .get(`${API}/contact`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => toast.error("Could not load enquiries"));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(status === "contacted" ? "Marked as contacted." : "Marked as read.");
    } catch {
      toast.error("Could not update enquiry.");
    }
  };

  if (!enquiries) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
        <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> Loading enquiries…
      </div>
    );
  }

  const filtered = filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter);
  const counts = enquiries.reduce(
    (acc, e) => {
      acc[e.status || "new"] = (acc[e.status || "new"] || 0) + 1;
      return acc;
    },
    { new: 0, read: 0, contacted: 0 }
  );

  return (
    <div data-testid="enquiries-content">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-white text-2xl font-bold uppercase tracking-tight">
            Enquiries inbox
          </h2>
          <p className="text-zinc-500 text-sm font-light mt-1">
            {counts.new} new · {counts.read} read · {counts.contacted} contacted
          </p>
        </div>
        <div className="flex items-center border border-zinc-800">
          {["all", "new", "read", "contacted"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`enquiries-filter-${f}`}
              className={`px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                filter === f ? "bg-[#D4AF37] text-zinc-950" : "text-zinc-400 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="enquiries-empty-state" className="border border-dashed border-zinc-800 p-14 text-center">
          <p className="font-display text-zinc-400 text-xl font-bold uppercase tracking-tight">
            {filter === "all" ? "No enquiries yet" : `No ${filter} enquiries`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const status = e.status || "new";
            return (
              <div
                key={e.id}
                data-testid={`enquiry-card-${e.id}`}
                className={`bg-zinc-900/50 border p-6 transition-colors ${
                  status === "new" ? "border-[#D4AF37]/40" : "border-zinc-800"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-display text-white text-lg font-bold uppercase tracking-wide">
                        {e.name}
                      </p>
                      <span
                        className={`px-2.5 py-0.5 text-[10px] font-bold tracking-[0.18em] uppercase ${STATUS_STYLES[status]}`}
                        data-testid={`enquiry-status-${e.id}`}
                      >
                        {status}
                      </span>
                      {e.service && (
                        <span className="text-zinc-500 text-[10px] font-semibold tracking-[0.16em] uppercase border border-zinc-800 px-2 py-0.5">
                          {e.service}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-zinc-400">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
                        <Mail size={13} /> {e.email}
                      </a>
                      {e.phone && (
                        <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
                          <Phone size={13} /> {e.phone}
                        </a>
                      )}
                      <span className="text-zinc-600 text-xs">{formatDate(e.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status === "new" && (
                      <button
                        onClick={() => setStatus(e.id, "read")}
                        data-testid={`mark-read-${e.id}`}
                        className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                      >
                        <MailOpen size={12} /> Mark Read
                      </button>
                    )}
                    {status !== "contacted" && (
                      <button
                        onClick={() => setStatus(e.id, "contacted")}
                        data-testid={`mark-contacted-${e.id}`}
                        className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3CE56] text-zinc-950 px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                      >
                        <PhoneCall size={12} /> Contacted
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-zinc-300 text-sm font-light leading-relaxed border-t border-zinc-800 pt-4">
                  {e.message}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
